// GPS trace logger page (U19).
//
// Records every fix the phone's Geolocation API delivers while driving, plus
// a passenger's taps as planned houses go by, into IndexedDB through the
// trace recorder. Export hands one JSON file per drive to the share sheet or
// a download. Nothing on this page talks to a server (KTD18, KTD19).
//
// Used one-handed by a passenger in a moving car: big tap targets, black on
// white, no animation. The fix age and accuracy update every second so a
// dead GPS watch is visible during the drive.

import @{}
  imm, imm_set, imm_html
from 'imm-dom'
import * as tr from '../trace-recorder.jsy'

const h = imm_html

const FLUSH_MS = 5000
const TICK_MS = 1000
const STALE_MS = 5000
const POOR_ACCURACY_M = 30
const MPH_PER_MPS = 2.236936
const GEO_OPTS = @{} enableHighAccuracy: true, maximumAge: 0, timeout: 15000

const store = tr.create_idb_store()
const $refs = {}

// The current drive's recorder; a new one is made at each start. planned
// holds the typed list while no drive is recording.
let rec = null
let planned = []
let watch_id = null
let wake = null
let watch_note = null
let save_note = null


function recording() ::
  return !! rec && 'recording' === rec.get_status()


function on_start() ::
  if recording() :: return
  const t = Date.now()
  rec = tr.create_recorder @:
    store: store
    user_agent: navigator.userAgent
  rec.plan @ planned
  rec.start @ t
  watch_note = null
  start_watch()
  wake_acquire()
  safe_flush().then(refresh_drives)
  if navigator.storage && navigator.storage.persist :: navigator.storage.persist()
  update()

async function on_stop() ::
  if ! recording() :: return
  if ! confirm('Stop recording this drive?') :: return
  stop_watch()
  // Stop first and release the wake lock after, without waiting on it: a
  // release that never settles (Safari) must not leave the drive recording.
  rec.stop @ Date.now()
  planned = rec.trace.planned
  const lock = wake
  wake = null
  if lock :: lock.release().catch @ err => rec.wakelock('failed', Date.now(), err.name || String(err))
  update()
  await safe_flush()
  refresh_drives()

function start_watch() ::
  if ! navigator.geolocation ::
    rec.event @ 'no_geolocation', Date.now()
    watch_note = 'This browser has no geolocation.'
    return
  watch_id = navigator.geolocation.watchPosition(on_fix, on_geo_error, GEO_OPTS)

function stop_watch() ::
  if null !== watch_id :: navigator.geolocation.clearWatch(watch_id)
  watch_id = null

function on_fix(position) ::
  const t = Date.now()
  if ! rec :: return
  rec.fix @ position, t
  update_fix()

function on_geo_error(err) ::
  const t = Date.now()
  if ! rec :: return
  const lost = rec.error(err, t)
  if lost ::
    stop_watch()
    watch_note = 'GPS WATCH LOST: location permission denied.'
  update()


async function wake_acquire() ::
  if ! recording() :: return
  if wake && ! wake.released :: return
  if ! navigator.wakeLock ::
    rec.wakelock @ 'unsupported', Date.now()
    return
  try ::
    const lock = await navigator.wakeLock.request('screen')
    // Stopped while the request was pending: let the lock go at once.
    if ! recording() ::
      lock.release().catch(() => {})
      return
    wake = lock
    rec.wakelock @ 'acquired', Date.now()
    lock.addEventListener @ 'release', on_wake_release
  catch err ::
    rec.wakelock @ 'failed', Date.now(), err.name || String(err)
  update()

function on_wake_release(evt) ::
  if recording() :: rec.wakelock @ 'released', Date.now()
  // A late release from an earlier lock must not drop the current one.
  if evt.target === wake :: wake = null
  update()

function on_visibility() ::
  const t = Date.now()
  const state = document.visibilityState
  if recording() :: rec.visibility @ state, t
  if 'visible' === state :: wake_acquire()
  else flush()

function flush() ::
  if rec && rec.is_dirty() :: safe_flush()

// Save the trace; a failure is shown, never left as an unhandled rejection.
// The trace stays dirty, so the periodic flush tries again.
async function safe_flush() ::
  try ::
    await rec.flush()
    save_note = null
  catch err ::
    save_note = `DRIVE NOT SAVED YET (${err.name || err}): keep this page open; it retries.`
  update()


function on_set_list() ::
  const numbers = tr.parse_planned($refs.plan_text.value)
  if recording() ::
    if rec.trace.marks.length && ! confirm('Replace the list? Marks so far are cleared.') :: return
    rec.plan @ numbers, Date.now()
  planned = numbers
  update()

function on_pass() ::
  if recording() && rec.mark(Date.now()) :: update_marks()

function on_skip() ::
  if recording() && rec.skip(Date.now()) :: update_marks()

function on_undo() ::
  if recording() && rec.undo(Date.now()) :: update_marks()


function on_export_current() ::
  if rec :: share_trace @ rec.to_json()

async function export_drive(drive_id) ::
  const trace = await store.get(drive_id)
  if trace :: share_trace @ trace

async function delete_drive(drive_id) ::
  if ! confirm(`Delete drive ${drive_id} from this phone?`) :: return
  await store.remove @ drive_id
  refresh_drives()

// The share sheet when it takes files, else a download. Either way the file
// goes where the operator sends it, never to our server.
async function share_trace(trace) ::
  const name = tr.export_filename(trace)
  const file_opts = @{} type: 'application/json'
  const file = new File([JSON.stringify(trace)], name, file_opts)
  const share = @{} files: [file], title: name
  if navigator.canShare && navigator.canShare(share) ::
    try ::
      await navigator.share @ share
      return
    catch err ::
      if 'AbortError' === err.name :: return
  download @ file

function download(file) ::
  const url = URL.createObjectURL(file)
  const $a = h.a @
    @{} href: url, download: file.name
  document.body.append @ $a
  $a.click()
  $a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 60000)


function fmt_time(t) ::
  return new Date(t).toLocaleTimeString([], {hour12: false})

function fmt_num(v, digits = 0) ::
  return null == v ? 'none' : v.toFixed(digits)

function update() ::
  update_status()
  update_fix()
  update_marks()
  const on = recording()
  imm($refs.start, {disabled: on})
  imm($refs.stop, {disabled: ! on})
  imm($refs.export, {disabled: ! rec})

function update_status() ::
  const status = rec ? rec.get_status() : 'idle'
  let text = 'IDLE. Enter the list, then Start.'
  if 'recording' === status ::
    text = `RECORDING since ${fmt_time(rec.trace.started_at)}`
  else if 'stopped' === status ::
    text = `STOPPED ${rec.trace.drive_id}. Export it below.`
  imm_set @ $refs.status, text

  const warn = []
  if watch_note :: warn.push @ watch_note
  if save_note :: warn.push @ save_note
  if recording() && ! wake :: warn.push @ 'Screen lock not held: keep the screen on.'
  imm_set @ $refs.warn, warn.join(' ')
  imm($refs.warn, {hidden: ! warn.length})

function update_fix() ::
  const f = rec ? rec.get_last_fix() : null
  const age = rec ? rec.fix_age(Date.now()) : null
  const stale = recording() && (null == age || age > STALE_MS)
  const poor = !! f && null != f.accuracy && f.accuracy > POOR_ACCURACY_M
  imm($refs.fix, {class: stale ? 'fix stale' : 'fix'})
  imm_set @ $refs.age, null == age ? 'no fix yet' : `${(age / 1000).toFixed(1)} s`
  imm_set @ $refs.acc, f ? `${fmt_num(f.accuracy)} m${poor ? ' POOR' : ''}` : 'none'
  imm_set @ $refs.hdg, f ? fmt_num(f.heading) : 'none'
  imm_set @ $refs.spd, f && null != f.speed ? `${(f.speed * MPH_PER_MPS).toFixed(0)} mph` : 'none'
  imm_set @ $refs.count, rec ? String(rec.trace.fixes.length) : '0'

function update_marks() ::
  const list = rec ? rec.trace.planned : planned
  const marks = rec ? rec.trace.marks : []
  const p = rec ? rec.progress() : null
  const on = recording()

  let label = 'NO LIST'
  if p && p.finished :: label = 'LIST FINISHED'
  else if p && null !== p.next :: label = `PASSED ${p.next}`
  else if list.length :: label = `NEXT ${list[0]}`
  imm_set @ $refs.pass, label
  imm($refs.pass, {disabled: ! on || ! p || null === p.next})
  imm($refs.skip, {disabled: ! on || ! p || null === p.next})
  imm($refs.undo, {disabled: ! on || ! marks.length})

  const items = list.map @ (n, i) => list_item(n, i, marks, p)
  imm_set @ $refs.list, items

function list_item(n, i, marks, p) ::
  const m = marks[i]
  let state = ''
  if m && 'pass' === m.kind :: state = `passed ${fmt_time(m.t)}`
  else if m :: state = 'skipped'
  else if p && i === p.index :: state = 'NEXT'
  return h.li @
    @{} class: p && i === p.index ? 'next' : ''
    h.b @ String(n)
    h.span @ state

async function refresh_drives() ::
  let list = []
  try ::
    list = await store.list()
  catch err ::
    imm_set @ $refs.drives, h.li(`Storage unavailable: ${err.name || err}`)
    return
  if ! list.length ::
    imm_set @ $refs.drives, h.li('No saved drives.')
    return
  imm_set @ $refs.drives, list.map(drive_item)

function drive_item(d) ::
  const when = d.started_at ? new Date(d.started_at).toLocaleString() : d.drive_id
  return h.li @
    @{} class: 'drive'
    h.div @ `${when}: ${d.fixes} fixes, ${d.marks} marks${d.watch_lost ? ', watch lost' : ''}`
    h.button @
      @{} class: 'small', click: () => export_drive(d.drive_id)
      'Export'
    h.button @
      @{} class: 'small', click: () => delete_drive(d.drive_id)
      'Delete'


function row(label, $value) ::
  return h.div @
    @{} class: 'row'
    h.span @ label
    $value

// The prototype page's data token (client/proto/region.jsy keeps it under this
// key). The prototype asks for it whenever the data is refused; this lets the
// operator replace or clear it by hand. The logger itself never uses it.
const TOKEN_KEY = 'simplemap-token'

function on_token_save() ::
  const v = $refs.token.value.trim()
  try ::
    if v :: localStorage.setItem(TOKEN_KEY, v)
    else localStorage.removeItem(TOKEN_KEY)
  catch err ::
    imm_set @ $refs.token_note, `Could not save: ${err.name || err}`
    return
  $refs.token.value = ''
  imm_set @ $refs.token_note, v ? 'Token saved on this phone.' : 'Token cleared.'

function render($app) ::
  $refs.status = h.div({class: 'status'})
  $refs.warn = h.div({class: 'warn', hidden: true})
  $refs.age = h.b()
  $refs.acc = h.b()
  $refs.hdg = h.b()
  $refs.spd = h.b()
  $refs.count = h.b()
  $refs.fix = h.section @
    @{} class: 'fix'
    row('Last fix age', $refs.age)
    row('Accuracy', $refs.acc)
    row('Heading', $refs.hdg)
    row('Speed', $refs.spd)
    row('Fixes', $refs.count)
  $refs.pass = h.button @
    @{} class: 'pass', click: on_pass
    'NO LIST'
  $refs.undo = h.button @
    @{} class: 'mid', click: on_undo
    'Undo'
  $refs.skip = h.button @
    @{} class: 'mid', click: on_skip
    'Skip'
  $refs.list = h.ol({class: 'planned'})
  $refs.plan_text = h.textarea @
    @{} rows: 4, placeholder: 'House numbers to pass, one per line: 1204 Main St'
  $refs.start = h.button @
    @{} class: 'mid', click: on_start
    'Start'
  $refs.stop = h.button @
    @{} class: 'mid', click: on_stop
    'Stop'
  $refs.export = h.button @
    @{} class: 'mid', click: on_export_current
    'Export'
  $refs.drives = h.ul({class: 'drives'})
  $refs.token = h.input @
    @{} type: 'password', autocomplete: 'off', placeholder: 'Data token for the prototype map'
  $refs.token_note = h.p()

  imm_set @
    $app
    h.a @
      @{} class: 'back', href: '../proto/'
      'Back to map'
    $refs.status
    $refs.warn
    $refs.fix
    $refs.pass
    h.div @
      @{} class: 'pair'
      $refs.undo
      $refs.skip
    $refs.list
    h.div @
      @{} class: 'trio'
      $refs.start
      $refs.stop
      $refs.export
    h.section @
      @{} class: 'plan'
      h.h2 @ 'Planned houses'
      $refs.plan_text
      h.button @
        @{} class: 'mid', click: on_set_list
        'Set list'
    h.section @
      h.h2 @ 'Saved drives'
      $refs.drives
      h.p @ 'Traces stay on this phone until you export them. Nothing is sent to a server.'
    h.section @
      @{} class: 'plan'
      h.h2 @ 'Prototype data token'
      $refs.token
      h.button @
        @{} class: 'mid', click: on_token_save
        'Save token (empty clears it)'
      $refs.token_note


function main() ::
  render @ document.getElementById('app')
  document.addEventListener @ 'visibilitychange', on_visibility
  window.addEventListener @ 'pagehide', flush
  setInterval(update_fix, TICK_MS)
  setInterval(flush, FLUSH_MS)
  update()
  refresh_drives()

main()
